import { Link } from 'react-router-dom';
import ServicesSubMenu from '../ServicesSubMenu';
import robotImage from '../../assets/images/robot1.webp';
import '../ServicesWebsites/index.css';

const outcomes = [
  { value: '24/7', label: 'Instant customer answers' },
  { value: '70%', label: 'Potential support labor reduction' },
  { value: '90%', label: 'Common FAQ coverage goal' },
];

const features = [
  'Website chatbot trained on your business information, FAQs, policies, and service details.',
  'SMS support so customers can text common questions and receive useful replies quickly.',
  'Human handoff for questions that need staff review, judgment, or follow-up.',
  'Admin-friendly review of unanswered questions so your knowledge base improves over time.',
  'AWS-hosted architecture designed for reliability, security, monitoring, and scale.',
];

const workflow = [
  {
    title: 'Load Your Knowledge',
    description:
      'Start with your existing FAQs, website pages, service descriptions, policies, and customer support notes.',
  },
  {
    title: 'Customers Ask',
    description:
      'Visitors ask questions through your website chatbot or by SMS when text messaging is part of the setup.',
  },
  {
    title: 'AI Finds the Answer',
    description:
      'The system searches your business knowledge base and drafts a practical answer in seconds.',
  },
  {
    title: 'Your Team Stays in Control',
    description:
      'Complex questions can be escalated, and new unanswered questions become candidates for future FAQ updates.',
  },
];

const useCases = [
  {
    title: 'E-Commerce and Retail',
    description:
      'Answer questions about orders, returns, product details, availability, store hours, and next steps before a customer abandons the purchase.',
  },
  {
    title: 'Local Services',
    description:
      'Help customers quickly understand pricing, availability, service areas, scheduling options, and how to request a quote.',
  },
  {
    title: 'Professional Services',
    description:
      'Handle appointment basics, location details, intake instructions, insurance notes, and common account questions without slowing down your staff.',
  },
  {
    title: 'Software and Membership Sites',
    description:
      'Reduce repetitive onboarding, billing, login, and feature questions while routing unusual issues to the right person.',
  },
];

const ServicesChatbots = () => {
  return (
    <div className="services-placeholder-page chatbot-service-page">
      <ServicesSubMenu />

      <section className="chatbot-hero" aria-labelledby="chatbot-service-title">
        <div className="chatbot-hero-copy">
          <p className="services-placeholder-kicker">AI Customer Support</p>
          <h1 id="chatbot-service-title">AI Integrated Intelligent Chatbots</h1>
          <p>
            Hogcloud builds website and SMS chatbot systems that answer repetitive
            customer questions, reduce support load, and keep your team focused on the
            conversations that actually need a person.
          </p>
          <div className="chatbot-hero-actions">
            <Link className="chatbot-primary-link" to="/contact">Request a Demo</Link>
            <Link className="chatbot-secondary-link" to="/services/websites">Back to Website Services</Link>
          </div>
        </div>
        <img src={robotImage} alt="Hogcloud AI chatbot service" />
      </section>

      <section className="chatbot-outcomes" aria-label="Chatbot service outcomes">
        {outcomes.map((outcome) => (
          <article key={outcome.label}>
            <strong>{outcome.value}</strong>
            <span>{outcome.label}</span>
          </article>
        ))}
      </section>

      <section className="chatbot-section chatbot-two-column" aria-labelledby="chatbot-problem-title">
        <div>
          <p className="services-placeholder-kicker">The Problem</p>
          <h2 id="chatbot-problem-title">Your team should not have to answer the same questions all day.</h2>
        </div>
        <div className="chatbot-copy-stack">
          <p>
            Many businesses lose time to the same support questions over email, phone,
            website forms, and text messages. Customers want fast answers about hours,
            pricing, scheduling, order status, returns, policies, and account access.
          </p>
          <p>
            A well-built chatbot does not replace your team. It filters the repetitive
            work, answers the known questions, and gives staff more time for complex
            customer needs.
          </p>
        </div>
      </section>

      <section className="chatbot-section" aria-labelledby="chatbot-features-title">
        <p className="services-placeholder-kicker">What Hogcloud Builds</p>
        <h2 id="chatbot-features-title">A practical support system for web and SMS.</h2>
        <div className="chatbot-feature-grid">
          {features.map((feature) => (
            <article key={feature}>
              <p>{feature}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="chatbot-section" aria-labelledby="chatbot-workflow-title">
        <p className="services-placeholder-kicker">How It Works</p>
        <h2 id="chatbot-workflow-title">From business knowledge to customer answers.</h2>
        <div className="chatbot-workflow">
          {workflow.map((step, index) => (
            <article key={step.title}>
              <span>{index + 1}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="chatbot-section" aria-labelledby="chatbot-industries-title">
        <p className="services-placeholder-kicker">Use Cases</p>
        <h2 id="chatbot-industries-title">Useful across customer-heavy businesses.</h2>
        <div className="chatbot-use-case-grid">
          {useCases.map((useCase) => (
            <article key={useCase.title}>
              <h3>{useCase.title}</h3>
              <p>{useCase.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="chatbot-section chatbot-two-column chatbot-architecture" aria-labelledby="chatbot-architecture-title">
        <div>
          <p className="services-placeholder-kicker">Technology</p>
          <h2 id="chatbot-architecture-title">Built on cloud services that are meant to operate.</h2>
        </div>
        <div className="chatbot-copy-stack">
          <p>
            Hogcloud can design the system around AWS services such as Lambda,
            DynamoDB, S3, CloudFront, Secrets Manager, and managed AI model access,
            with Twilio available when SMS is part of the workflow.
          </p>
          <p>
            The result is a service that can start small, stay maintainable, and grow
            into admin dashboards, reporting, broadcast messaging, or deeper customer
            account integrations.
          </p>
        </div>
      </section>

      <section className="chatbot-cta" aria-labelledby="chatbot-cta-title">
        <h2 id="chatbot-cta-title">Ready to make customer questions easier to handle?</h2>
        <p>
          Hogcloud can review your current FAQ workload and map the first chatbot or SMS
          support workflow that would give your team time back.
        </p>
        <Link className="chatbot-primary-link" to="/contact">Start the Conversation</Link>
      </section>
    </div>
  );
};

export default ServicesChatbots;
